import { useState, useEffect, useCallback } from 'react';
import Card from '../components/Card';
import Button from '../components/Button';
import { api } from '../lib/api';
import type { Employee, Team, TeamMember, ManagerToolType } from '../lib/types';

const TOOL_OPTIONS: Array<{ key: ManagerToolType; icon: string; label: string; desc: string }> = [
  { key: 'monday_voice', icon: 'sun', label: '月曜朝の声かけ', desc: 'タイプ別に最適な朝の声かけを生成' },
  { key: 'oneonone_prep', icon: 'users', label: '1on1 準備', desc: '部下のタイプに合った質問リストと進め方' },
  { key: 'weekly_tips', icon: 'calendar', label: '週例 Tips', desc: 'チーム構成に合った会議の進め方' },
];

function ToolIcon({ name, size = 24 }: { name: string; size?: number }) {
  const props = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (name) {
    case 'sun': return <svg {...props}><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>;
    case 'users': return <svg {...props}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
    case 'calendar': return <svg {...props}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
    default: return null;
  }
}

export default function ManagerTools() {
  const [selectedTool, setSelectedTool] = useState<ManagerToolType>('monday_voice');
  const [targetType, setTargetType] = useState<'individual' | 'team'>('individual');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [context, setContext] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [empRes, teamRes] = await Promise.all([
        api.employees.list(),
        api.teams.list(),
      ]);
      const emps = empRes.employees as unknown as Employee[];
      setEmployees(emps.filter(e => e.personality_type && e.consent_given));
      setTeams(teamRes.teams as unknown as Team[]);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Load team members when team is selected
  useEffect(() => {
    if (selectedTeamId) {
      api.teams.get(selectedTeamId).then(res => {
        setTeamMembers(res.members as unknown as TeamMember[]);
      }).catch(() => {});
    }
  }, [selectedTeamId]);

  const [cached, setCached] = useState(false);

  const generate = async (forceRefresh = false) => {
    setLoading(true);
    setError('');
    if (!forceRefresh) setResult(null);
    try {
      const res = await api.ai.managerTool({
        tool_type: selectedTool,
        target_type: targetType,
        employee_id: targetType === 'individual' ? selectedEmployeeId : undefined,
        team_id: targetType === 'team' ? selectedTeamId : undefined,
        context: context || undefined,
        force_refresh: forceRefresh || undefined,
      });
      setResult(res.result);
      setCached(res.cached);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const canGenerate = targetType === 'individual' ? !!selectedEmployeeId : !!selectedTeamId;

  return (
    <div>
      <h2 className="text-xl lg:text-2xl font-bold text-text-main mb-5">マネージャーツール</h2>

      {/* Tool Selection */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        {TOOL_OPTIONS.map(tool => (
          <Card
            key={tool.key}
            onClick={() => { setSelectedTool(tool.key); setResult(null); setCached(false); }}
            className={`text-center ${selectedTool === tool.key ? '!border-primary bg-primary-bg' : ''}`}
          >
            <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg mb-2 ${selectedTool === tool.key ? 'bg-primary/20 text-primary' : 'bg-surface text-text-muted'}`}>
              <ToolIcon name={tool.icon} size={20} />
            </div>
            <div className="text-sm font-semibold text-text-main">{tool.label}</div>
            <div className="text-[11px] text-text-muted mt-1">{tool.desc}</div>
          </Card>
        ))}
      </div>

      {/* Target Selection */}
      <Card className="mb-5">
        <div className="flex items-center gap-4 mb-4">
          <label className="text-sm text-text-sub">対象:</label>
          <button
            onClick={() => { setTargetType('individual'); setResult(null); setCached(false); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              targetType === 'individual' ? 'bg-primary text-white' : 'bg-surface text-text-sub hover:bg-surface-hover'
            }`}
          >
            個人
          </button>
          <button
            onClick={() => { setTargetType('team'); setResult(null); setCached(false); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              targetType === 'team' ? 'bg-primary text-white' : 'bg-surface text-text-sub hover:bg-surface-hover'
            }`}
          >
            チーム
          </button>
        </div>

        {targetType === 'individual' ? (
          <select
            value={selectedEmployeeId}
            onChange={e => { setSelectedEmployeeId(e.target.value); setResult(null); setCached(false); }}
            className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-text-main focus:border-primary outline-none"
          >
            <option value="">社員を選択...</option>
            {employees.map(e => (
              <option key={e.id} value={e.id}>{e.name} ({e.personality_type})</option>
            ))}
          </select>
        ) : (
          <div>
            <select
              value={selectedTeamId}
              onChange={e => { setSelectedTeamId(e.target.value); setResult(null); setCached(false); }}
              className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-text-main focus:border-primary outline-none"
            >
              <option value="">チームを選択...</option>
              {teams.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            {teamMembers.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {teamMembers.map(m => (
                  <span key={m.id} className="text-[11px] bg-surface px-2 py-0.5 rounded text-text-sub">
                    {m.name} ({m.personality_type})
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Context input */}
        <div className="mt-3">
          <label className="text-xs text-text-muted mb-1 block">補足情報（任意）</label>
          <input
            type="text"
            value={context}
            onChange={e => setContext(e.target.value)}
            placeholder="例: 先週遅刻が目立った、来月大きなプレゼンがある..."
            className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-text-main focus:border-primary outline-none placeholder:text-text-muted"
          />
        </div>

        <Button primary className="w-full mt-4" onClick={() => generate()} disabled={!canGenerate || loading}>
          {loading ? 'AI生成中...' : 'AIで生成する'}
        </Button>
      </Card>

      {/* Error */}
      {error && (
        <Card className="!border-accent/30 bg-accent/5 mb-4">
          <p className="text-sm text-accent">{error}</p>
        </Card>
      )}

      {/* Results */}
      {result && (
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {cached && (
              <span className="text-[11px] text-text-muted bg-surface px-2 py-0.5 rounded border border-border">
                キャッシュから取得
              </span>
            )}
          </div>
          <button
            onClick={() => generate(true)}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium text-primary border border-primary/30 hover:bg-primary/5 transition-colors disabled:opacity-50"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
            {loading ? '再生成中...' : '再生成する'}
          </button>
        </div>
      )}
      {result && <ResultDisplay toolType={selectedTool} targetType={targetType} data={result} />}
    </div>
  );
}

// ─── Result Display Components ───

function ResultDisplay({ toolType, targetType, data }: {
  toolType: ManagerToolType;
  targetType: 'individual' | 'team';
  data: Record<string, unknown>;
}) {
  if (toolType === 'monday_voice') return <MondayVoiceResult data={data} targetType={targetType} />;
  if (toolType === 'oneonone_prep') return <OneOnOneResult data={data} />;
  if (toolType === 'weekly_tips') return <WeeklyTipsResult data={data} />;
  return null;
}

function MondayVoiceResult({ data, targetType }: { data: Record<string, unknown>; targetType: string }) {
  if (targetType === 'individual') {
    const options = (data.voice_options || []) as Array<{ message: string; tone: string; why_effective: string }>;
    const ng = data.ng_example as { message: string; why_ng: string } | undefined;
    return (
      <div className="space-y-3">
        <Card>
          <div className="text-[15px] font-semibold text-text-main mb-3">声かけオプション</div>
          {options.map((opt, i) => (
            <div key={i} className="mb-3 p-3 rounded-lg bg-surface border border-border">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded font-semibold">{opt.tone}</span>
              </div>
              <p className="text-sm text-text-main leading-relaxed mb-1.5">「{opt.message}」</p>
              <p className="text-xs text-text-sub">{opt.why_effective}</p>
            </div>
          ))}
        </Card>
        {ng && (
          <Card className="!border-accent/20">
            <div className="text-xs font-semibold text-accent mb-2">NG例</div>
            <p className="text-sm text-text-main mb-1">「{ng.message}」</p>
            <p className="text-xs text-text-sub">{ng.why_ng}</p>
          </Card>
        )}
        {!!data.monday_tip && (
          <Card>
            <div className="text-xs font-semibold text-primary mb-1">Tips</div>
            <p className="text-sm text-text-sub">{String(data.monday_tip)}</p>
          </Card>
        )}
      </div>
    );
  }

  // Team
  const teamMsg = data.team_message as { message: string; approach: string } | undefined;
  const followUps = (data.individual_follow_ups || []) as Array<{ member: string; type: string; private_message: string; reason: string }>;
  return (
    <div className="space-y-3">
      {teamMsg && (
        <Card>
          <div className="text-[15px] font-semibold text-text-main mb-3">チーム全体への声かけ</div>
          <p className="text-sm text-text-main leading-relaxed bg-surface p-3 rounded-lg border border-border">「{teamMsg.message}」</p>
          <p className="text-xs text-text-sub mt-2">{teamMsg.approach}</p>
        </Card>
      )}
      {followUps.length > 0 && (
        <Card>
          <div className="text-[15px] font-semibold text-text-main mb-3">個別フォロー</div>
          {followUps.map((f, i) => (
            <div key={i} className="mb-2 p-2.5 rounded-lg bg-surface border border-border">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold text-text-main">{f.member}</span>
                <span className="text-[10px] text-text-muted">({f.type})</span>
              </div>
              <p className="text-xs text-text-sub">{f.private_message}</p>
            </div>
          ))}
        </Card>
      )}
      {!!data.monday_tip && (
        <Card>
          <div className="text-xs font-semibold text-primary mb-1">Tips</div>
          <p className="text-sm text-text-sub">{String(data.monday_tip)}</p>
        </Card>
      )}
    </div>
  );
}

function OneOnOneResult({ data }: { data: Record<string, unknown> }) {
  const prep = data.preparation as { recommended_duration: string; recommended_setting: string; warmup_tip: string; advance_notice: string } | undefined;
  const questions = (data.questions || []) as Array<{ priority: number; question: string; purpose: string; why_effective: string; follow_up: string }>;
  const flowTips = (data.flow_tips || []) as string[];
  const dontList = (data.dont_list || []) as Array<{ action: string; reason: string }>;

  return (
    <div className="space-y-3">
      {prep && (
        <Card>
          <div className="text-[15px] font-semibold text-text-main mb-3">準備</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="bg-surface rounded-lg p-2.5 border border-border">
              <div className="text-[10px] text-text-muted mb-0.5">推奨時間</div>
              <div className="text-xs text-text-main">{prep.recommended_duration}</div>
            </div>
            <div className="bg-surface rounded-lg p-2.5 border border-border">
              <div className="text-[10px] text-text-muted mb-0.5">推奨環境</div>
              <div className="text-xs text-text-main">{prep.recommended_setting}</div>
            </div>
          </div>
          {prep.warmup_tip && <p className="text-xs text-text-sub mt-2">{prep.warmup_tip}</p>}
          {prep.advance_notice && <p className="text-xs text-primary mt-1">{prep.advance_notice}</p>}
        </Card>
      )}
      {questions.length > 0 && (
        <Card>
          <div className="text-[15px] font-semibold text-text-main mb-3">質問リスト</div>
          {questions.map((q, i) => (
            <div key={i} className="mb-3 p-3 rounded-lg bg-surface border border-border">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded font-bold">#{q.priority}</span>
                <span className="text-[10px] text-text-muted">{q.purpose}</span>
              </div>
              <p className="text-sm text-text-main leading-relaxed mb-1">「{q.question}」</p>
              <p className="text-xs text-text-sub mb-1">{q.why_effective}</p>
              {q.follow_up && <p className="text-xs text-text-muted">深掘り: {q.follow_up}</p>}
            </div>
          ))}
        </Card>
      )}
      {(flowTips.length > 0 || dontList.length > 0) && (
        <Card>
          {flowTips.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-semibold text-success mb-2">進め方 Tips</div>
              {flowTips.map((tip, i) => (
                <p key={i} className="text-xs text-text-sub mb-1 pl-3 border-l-2 border-success/30">{tip}</p>
              ))}
            </div>
          )}
          {dontList.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-accent mb-2">やってはいけないこと</div>
              {dontList.map((d, i) => (
                <div key={i} className="mb-1.5 pl-3 border-l-2 border-accent/30">
                  <p className="text-xs text-text-main">{d.action}</p>
                  <p className="text-[10px] text-text-muted">{d.reason}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
      {!!data.closing_tip && (
        <Card>
          <div className="text-xs font-semibold text-primary mb-1">締め方</div>
          <p className="text-sm text-text-sub">{String(data.closing_tip)}</p>
        </Card>
      )}
    </div>
  );
}

function WeeklyTipsResult({ data }: { data: Record<string, unknown> }) {
  const profile = data.team_profile as { dominant_traits: string; communication_style: string } | undefined;
  const plan = data.meeting_plan as {
    recommended_duration: string;
    opening: { approach: string; script_example: string; tip: string };
    main: { approach: string; presentation_style: string; discussion_format: string; tip: string };
    closing: { approach: string; script_example: string; action_item_style: string };
  } | undefined;
  const notes = (data.individual_notes || []) as Array<{ member: string; type: string; note: string; action: string }>;
  const pitfalls = (data.common_pitfalls || []) as Array<{ problem: string; solution: string }>;

  return (
    <div className="space-y-3">
      {profile && (
        <Card>
          <div className="text-[15px] font-semibold text-text-main mb-2">チームプロファイル</div>
          <p className="text-sm text-text-sub">{profile.dominant_traits}</p>
          <p className="text-xs text-primary mt-1">{profile.communication_style}</p>
        </Card>
      )}
      {plan && (
        <Card>
          <div className="text-[15px] font-semibold text-text-main mb-3">会議プラン ({plan.recommended_duration})</div>
          {[
            { title: '冒頭', data: plan.opening, fields: ['approach', 'script_example', 'tip'] as const },
            { title: '本題', data: plan.main, fields: ['approach', 'presentation_style', 'discussion_format', 'tip'] as const },
            { title: '締めくくり', data: plan.closing, fields: ['approach', 'script_example', 'action_item_style'] as const },
          ].map((section) => (
            <div key={section.title} className="mb-3 p-3 rounded-lg bg-surface border border-border">
              <div className="text-xs font-bold text-primary mb-1.5">{section.title}</div>
              {section.fields.map(field => {
                const val = (section.data as Record<string, string>)[field];
                if (!val) return null;
                const label = field === 'script_example' ? 'セリフ例' : field === 'tip' ? 'コツ' : field === 'approach' ? '進め方' : field;
                return (
                  <div key={field} className="mb-1">
                    <span className="text-[10px] text-text-muted">{label}: </span>
                    <span className="text-xs text-text-sub">{val}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </Card>
      )}
      {notes.length > 0 && (
        <Card>
          <div className="text-[15px] font-semibold text-text-main mb-3">個別配慮メモ</div>
          {notes.map((n, i) => (
            <div key={i} className="mb-2 p-2.5 rounded-lg bg-surface border border-border">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold text-text-main">{n.member}</span>
                <span className="text-[10px] text-text-muted">({n.type})</span>
              </div>
              <p className="text-xs text-text-sub">{n.note}</p>
              <p className="text-[10px] text-primary mt-0.5">{n.action}</p>
            </div>
          ))}
        </Card>
      )}
      {pitfalls.length > 0 && (
        <Card>
          <div className="text-xs font-semibold text-warning mb-2">起きやすい問題</div>
          {pitfalls.map((p, i) => (
            <div key={i} className="mb-2 pl-3 border-l-2 border-warning/30">
              <p className="text-xs text-text-main">{p.problem}</p>
              <p className="text-[10px] text-text-sub">{p.solution}</p>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
